import React, { useCallback, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { supabase } from '../../supabaseClient';
import Modal from '../Modal';
import InfoIcon from '../InfoIcon';
import AutoSizeTextarea from '../AutoSizeTextarea';
import '../../lib/datepicker-theme.css';

// Combined reply + replace modal – opens from a Rejected ("Not accepted")
// or Needs-Clarification document on the supplier portal. The supplier can:
//   - type a reply, and/or
//   - upload a new copy (with an expiry date),
// and submit both in one action. Either alone is valid; both together is the
// point ("reply AND replace at the same time").
//
// Every submit appends a message to compliance_clarification_messages (the
// thread), so the full back-and-forth is kept. It also updates
// supplier_onboarding_documents: a new file re-uploads (→ Awaiting Review); a
// reply-only clears review_decision so the doc returns to Awaiting Review for
// the admin to re-decide. The latest supplier_reply column is kept in sync for
// backward-compat, but the thread is the source of truth for history.
//
// File/expiry rules mirror ComplianceUploadModal (PDF/JPG/PNG, 10MB, expiry in
// the future). The small date/file helpers are duplicated from that modal on
// purpose – this stays self-contained rather than coupling the two flows.

const MIN_REPLY_LENGTH = 10;
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_MIME = 'application/pdf,image/jpeg,image/png';
const ACCEPTED_EXTENSIONS_LABEL = 'PDF · JPG · PNG · Max 10MB';
const AMBER = '#E89A2E';

const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };

const evaluateExpiryDate = (date) => {
  if (!date) return { valid: false, message: 'Must be in the future', tone: 'grey' };
  const today = startOfToday();
  const daysOut = Math.round((date.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));
  if (daysOut <= 0) return { valid: false, message: 'Expiry must be in the future', tone: 'red' };
  if (daysOut > 365 * 10) return { valid: true, daysOut, message: "Are you sure? That's over 10 years away", tone: 'amber' };
  if (daysOut < 30) return { valid: true, daysOut, message: `In ${daysOut} day${daysOut === 1 ? '' : 's'} – this would be Expiring Soon`, tone: 'amber' };
  if (daysOut <= 60) return { valid: true, daysOut, message: `In ${daysOut} days from today`, tone: 'grey' };
  const months = Math.round(daysOut / 30);
  return { valid: true, daysOut, message: `${date.getFullYear()} – ${months} months from today`, tone: 'grey' };
};

const toIsoDateOnly = (d) => {
  if (!d) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const validateFile = (file) => {
  if (!file) return { ok: false, message: 'No file selected' };
  if (!ACCEPTED_MIME.split(',').includes(file.type)) return { ok: false, message: 'PDF, JPG, or PNG only' };
  if (file.size === 0) return { ok: false, message: 'File is empty' };
  if (file.size > MAX_FILE_BYTES) return { ok: false, message: 'File too large – max 10MB' };
  return { ok: true };
};

const formatFileSize = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : '0 MB');

const UploadIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#51534A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
const FileTextIcon = ({ stroke = AMBER }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="13" x2="15" y2="13" />
    <line x1="9" y1="17" x2="13" y2="17" />
  </svg>
);
const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={AMBER} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);
const Spinner = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true" className="animate-spin">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" fill="none" strokeOpacity="0.25" />
    <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" fill="none" strokeLinecap="round" />
  </svg>
);

export default function ComplianceRespondModal({ document: docMeta, record, supplierId, onSuccess, onClose }) {
  const [reply, setReply] = useState('');
  const [file, setFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState(null);
  const [state, setState] = useState('input'); // input | submitting | success | error
  const [errorMessage, setErrorMessage] = useState(null);
  const [filePickerError, setFilePickerError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const trimmedReply = reply.trim();
  const hasReply = trimmedReply.length > 0;
  const replyValid = !hasReply || trimmedReply.length >= MIN_REPLY_LENGTH;
  const hasFile = Boolean(file);
  // Same rule as the upload modal: only ask for an expiry where the document is
  // configured to ask the supplier for one. Otherwise the admin sets it at
  // review, and demanding it here blocks a reply-and-replace on a date the
  // supplier may have no way of knowing.
  const asksExpiry = docMeta?.supplierProvidesExpiry === true;
  const dateInfo = evaluateExpiryDate(expiryDate);
  const fileValid = !hasFile || !asksExpiry || dateInfo.valid;
  const canSubmit = (hasReply || hasFile) && replyValid && fileValid;
  const tomorrow = (() => { const d = startOfToday(); d.setDate(d.getDate() + 1); return d; })();

  const submitting = state === 'submitting';
  const success = state === 'success';

  useEffect(() => {
    if (state !== 'success') return;
    const t = setTimeout(() => onSuccess?.(), 2000);
    return () => clearTimeout(t);
  }, [state, onSuccess]);

  const acceptPicked = useCallback((picked) => {
    if (!picked) return;
    const v = validateFile(picked);
    if (!v.ok) { setFilePickerError(v.message); setTimeout(() => setFilePickerError(null), 3000); return; }
    setFilePickerError(null);
    setFile(picked);
  }, []);

  const handleFilePick = useCallback((e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    acceptPicked(picked);
  }, [acceptPicked]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    if (state === 'submitting') return;
    acceptPicked(e.dataTransfer?.files?.[0]);
  }, [acceptPicked, state]);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit || !supplierId || !docMeta?.key) return;
    setState('submitting');
    setErrorMessage(null);
    try {
      const nowIso = new Date().toISOString();
      let fileUrl = null;
      let fileName = null;

      if (hasFile) {
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const path = `${supplierId}/${docMeta.key}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from('supplier-uploads')
          .upload(path, file, { contentType: file.type, upsert: true });
        if (uploadError) throw new Error(uploadError.message);
        const { data: pub } = supabase.storage.from('supplier-uploads').getPublicUrl(path);
        fileUrl = pub?.publicUrl || null;
        fileName = file.name;

        // Re-upload: fresh file returns the doc to Awaiting Review and clears
        // the prior review (mirrors ComplianceUploadModal). Keep the reply on
        // the row too if one was typed alongside.
        const { error: upsertError } = await supabase
          .from('supplier_onboarding_documents')
          .upsert({
            supplier_id: supplierId,
            document_key: docMeta.key,
            supplier_uploaded_file_url: fileUrl,
            supplier_uploaded_file_name: file.name,
            supplier_uploaded_at: nowIso,
            expiry_date: toIsoDateOnly(expiryDate),
            expiry_approved_at: null,
            expiry_approved_by: null,
            expiry_email_sent_at: null,
            expiry_warning_email_sent_at: null,
            signed_at: null,
            signed_marked_by: null,
            review_decision: null,
            review_reason: null,
            review_notes: null,
            reviewed_at: null,
            reviewed_by: null,
            supplier_reply: hasReply ? trimmedReply : null,
            supplier_reply_at: hasReply ? nowIso : null,
            updated_at: nowIso,
          }, { onConflict: 'supplier_id,document_key' });
        if (upsertError) throw new Error(upsertError.message);
      } else {
        // Reply only: clear review_decision so the doc leaves Needs
        // Clarification / Rejected and returns to Awaiting Review.
        const { error: updateError } = await supabase
          .from('supplier_onboarding_documents')
          .update({
            supplier_reply: trimmedReply,
            supplier_reply_at: nowIso,
            review_decision: null,
            updated_at: nowIso,
          })
          .eq('supplier_id', supplierId)
          .eq('document_key', docMeta.key);
        if (updateError) throw new Error(updateError.message);
      }

      // Append to the thread (source of truth for history).
      let createdBy = null;
      try { const { data: { user } } = await supabase.auth.getUser(); createdBy = user?.id || null; } catch { /* best-effort */ }
      const { error: msgError } = await supabase
        .from('compliance_clarification_messages')
        .insert({
          supplier_id: supplierId,
          document_key: docMeta.key,
          author: 'supplier',
          body: hasReply ? trimmedReply : null,
          attachment_url: fileUrl,
          attachment_name: fileName,
          created_by: createdBy,
        });
      if (msgError) throw new Error(msgError.message);

      setState('success');
    } catch (err) {
      setErrorMessage(err?.message || 'Could not submit. Try again.');
      setState('error');
    }
  }, [canSubmit, supplierId, docMeta?.key, hasFile, file, expiryDate, hasReply, trimmedReply]);

  const safeClose = useCallback(() => { if (submitting) return; onClose?.(); }, [submitting, onClose]);
  const clearError = () => { if (state === 'error') setState('input'); };

  const latestAsk = record?.review_notes || record?.review_reason || docMeta.description;

  if (success) {
    return (
      <Modal open onClose={onSuccess} maxWidth={440} stripe={false} ariaLabel="Response sent">
        <div className="px-7 py-10 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-4" style={{ backgroundColor: 'rgba(232, 154, 46, 0.1)', border: '1.5px solid rgba(232, 154, 46, 0.4)' }}>
            <CheckIcon />
          </div>
          <h2 className="font-['Metropolis'] text-lg font-extrabold text-ink mb-2">Sent to Fusion 2K</h2>
          <p className="font-['Metropolis'] text-[12px] text-fusion-mid-grey leading-relaxed max-w-[320px] mx-auto mb-5">
            {hasFile
              ? `Your ${docMeta.label} is back with Fusion 2K for review.`
              : `Fusion 2K will look at your reply alongside your ${docMeta.label}.`}
          </p>
          <p className="font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.18em] text-[#51534A]">Closing in 2 seconds…</p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal open onClose={safeClose} maxWidth={480} stripe={false} ariaLabel={`Respond to Fusion 2K – ${docMeta.label}`}>
      <Modal.Header
        eyebrow="Respond to Fusion 2K"
        title={docMeta.label}
        sub={latestAsk}
        onClose={submitting ? undefined : safeClose}
      />
      <Modal.Body>
        <p className="font-['Metropolis'] text-[11px] text-[#51534A] mb-4 leading-snug">
          Reply, upload a new copy, or do both – whatever answers Fusion 2K's request.
        </p>

        {/* Reply */}
        <div className="mb-5">
          <label htmlFor="compliance-respond-text" className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2">
            Your reply <span className="text-[#9BA08C] normal-case tracking-normal font-semibold">· optional</span>
          </label>
          <AutoSizeTextarea
            id="compliance-respond-text"
            value={reply}
            onChange={(e) => { setReply(e.target.value); clearError(); }}
            disabled={submitting}
            minRows={4}
            placeholder="Address Fusion 2K's request – they'll see this alongside the document."
            className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[13px] text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#E89A2E] disabled:opacity-50" />
          {hasReply && !replyValid && (
            <p className="font-['Metropolis'] text-[11px] mt-1.5" style={{ color: AMBER }}>
              {MIN_REPLY_LENGTH - trimmedReply.length} more character{MIN_REPLY_LENGTH - trimmedReply.length === 1 ? '' : 's'} to send a reply.
            </p>
          )}
        </div>

        {/* Replacement file */}
        <div className="mb-3">
          <label className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2">
            Upload a new copy <span className="text-[#9BA08C] normal-case tracking-normal font-semibold">· optional</span>
          </label>
          <input ref={fileInputRef} type="file" accept={ACCEPTED_MIME} onChange={handleFilePick} disabled={submitting} style={{ display: 'none' }} />
          {!file ? (
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => { e.preventDefault(); if (!submitting) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                disabled={submitting}
                className={`w-full bg-transparent border-2 border-dashed rounded-lg px-4 py-5 flex flex-col items-center justify-center gap-1.5 cursor-pointer transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${dragOver ? 'border-[#E89A2E] bg-[#E89A2E]/[0.06]' : 'border-line hover:border-[#DADCCD]'}`}>
                <UploadIcon />
                <span className="font-['Metropolis'] text-[12px] font-bold text-fusion-mid-grey">{dragOver ? 'Drop to upload' : 'Click or drag a file to upload'}</span>
                <span className="font-['Metropolis'] text-[10px] text-[#51534A]">{ACCEPTED_EXTENSIONS_LABEL}</span>
              </button>
              {filePickerError && <p className="font-['Metropolis'] text-[11px] font-semibold mt-2" style={{ color: '#E0290A' }}>{filePickerError}</p>}
            </div>
          ) : (
            <div className="flex items-center gap-3 rounded-lg px-3 py-2.5" style={{ backgroundColor: 'rgba(232, 154, 46, 0.05)', border: '1px solid rgba(232, 154, 46, 0.28)', borderLeft: `3px solid ${AMBER}` }}>
              <div className="w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(232, 154, 46, 0.14)' }}>
                <FileTextIcon />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-['Metropolis'] text-[12px] text-ink font-bold truncate" title={file.name}>{file.name}</div>
                <div className="font-['Metropolis'] text-[10px] text-[#51534A]">{formatFileSize(file.size)}</div>
              </div>
              <button type="button" onClick={() => fileInputRef.current?.click()} disabled={submitting} className="font-['Metropolis'] text-[9px] font-bold uppercase tracking-[0.15em] text-fusion-mid-grey hover:text-ink bg-transparent border-0 cursor-pointer disabled:opacity-50">Change</button>
              <button type="button" onClick={() => { setFile(null); setFilePickerError(null); }} disabled={submitting} aria-label="Remove file" className="w-6 h-6 rounded flex items-center justify-center bg-transparent border border-line text-[#51534A] hover:text-[#E0290A] hover:border-[#E0290A]/60 transition-colors disabled:opacity-50">
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
          )}
        </div>

        {/* Expiry – only once a file is attached, AND only where the document
            asks the supplier for one rather than the admin setting it at
            review. */}
        {hasFile && asksExpiry && (
          <div className="mb-1">
            <label className="block font-['Metropolis'] text-[10px] font-bold uppercase tracking-[0.2em] text-[#51534A] mb-2">
              Expiry date
              <InfoIcon heading="Why we ask for an expiry date" ariaLabel="Why do you ask for an expiry date?">For documents that expire (like insurance), the expiry date lets Fusion 2K move you to 'Expiring Soon' in good time, so you can renew before it lapses.</InfoIcon>
            </label>
            <DatePicker
              selected={expiryDate}
              onChange={(d) => { setExpiryDate(d); clearError(); }}
              minDate={tomorrow}
              disabled={submitting}
              dateFormat="dd/MM/yyyy"
              placeholderText="dd/mm/yyyy"
              isClearable
              className="w-full bg-white border border-line rounded-md px-3 py-2.5 font-['Metropolis'] text-[14px] text-ink placeholder:text-[#51534A] focus:outline-none focus:border-[#E89A2E] disabled:opacity-50"
              wrapperClassName="block w-full" />
            <p className="font-['Metropolis'] text-[11px] mt-1.5 leading-snug" style={{ color: dateInfo.tone === 'red' ? '#E0290A' : dateInfo.tone === 'amber' ? AMBER : '#51534A' }}>
              {dateInfo.message}
            </p>
          </div>
        )}

        {state === 'error' && errorMessage && (
          <div role="alert" className="mt-4 rounded-md px-3 py-2.5 font-['Metropolis'] text-[12px]" style={{ backgroundColor: 'rgba(224, 41, 10, 0.08)', border: '1px solid rgba(224, 41, 10, 0.35)', color: '#E0290A' }}>
            Couldn't submit. {errorMessage}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="flex items-center justify-between gap-3">
          <button type="button" onClick={safeClose} disabled={submitting} className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#51534A] hover:text-fusion-mid-grey bg-transparent border-0 cursor-pointer disabled:opacity-50">Cancel</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.15em] text-[#131313] bg-[#E89A2E] border border-[#E89A2E] rounded-full px-5 py-2 inline-flex items-center gap-2 transition-[filter,opacity] duration-150 hover:brightness-110 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:brightness-100">
            {submitting ? (<><Spinner /><span>Sending…</span></>) : <span>{hasFile && hasReply ? 'Send reply & new copy' : hasFile ? 'Send new copy' : 'Send reply'}</span>}
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
